import axios from "axios";
import { useMutation, useQuery } from "@tanstack/react-query";

import { __API_URL__ } from "../SERVER_URL";

const startStudyExport = async ({ trialId, reportKinds, includePreScreeningAndScreenFailed }) => {
  const token = localStorage.getItem("token");
  const url = `${__API_URL__}/api/reports/study_export/start/`;

  const response = await axios.post(
    url,
    {
      trial_id: trialId,
      report_kinds: reportKinds,
      include_pre_screening_and_screen_failed: includePreScreeningAndScreenFailed,
    },
    {
      headers: {
        Authorization: `Token ${token}`,
        Timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      validateStatus: (s) => s === 202 || (s >= 200 && s < 300),
    }
  );

  if (response.status !== 202) {
    throw new Error(response.data?.detail || "Failed to start study export");
  }

  const exportRequestId = response.data?.export_request_id;
  if (!exportRequestId) throw new Error("Backend did not return export_request_id");

  return {
    exportRequestId,
    status: response.data.status,
    queuePosition: response.data.queue_position,
  };
};

export const getStudyExportStatus = async ({ exportRequestId, signal }) => {
  const token = localStorage.getItem("token");
  const url = `${__API_URL__}/api/reports/study_export/${exportRequestId}/status/`;

  const res = await axios.get(url, {
    signal,
    headers: { Authorization: `Token ${token}` },
  });

  return res.data;
};

const reportEcrfFilledPdfOutcome = async ({ exportRequestId, reason, error }) => {
  const token = localStorage.getItem("token");
  const url = `${__API_URL__}/api/reports/study_export/${exportRequestId}/ecrf_filled_pdf_failed/`;

  const response = await axios.post(
    url,
    { reason, error },
    { headers: { Authorization: `Token ${token}` } }
  );

  return response.data;
};

const reportPoemFilledPdfOutcome = async ({ exportRequestId, reportKind, reason, error }) => {
  const token = localStorage.getItem("token");
  const url = `${__API_URL__}/api/reports/study_export/${exportRequestId}/poem_filled_pdf_failed/`;

  const response = await axios.post(
    url,
    { report_kind: reportKind, reason, error },
    { headers: { Authorization: `Token ${token}` } }
  );

  return response.data;
};

const cancelStudyExport = async ({ exportRequestId }) => {
  const token = localStorage.getItem("token");
  const url = `${__API_URL__}/api/reports/study_export/${exportRequestId}/cancel/`;

  const response = await axios.post(url, null, {
    headers: { Authorization: `Token ${token}` },
  });

  return response.data;
};

export const useStartStudyExportMutation = () =>
  useMutation({
    mutationFn: ({ trialId, reportKinds, includePreScreeningAndScreenFailed }) =>
      startStudyExport({ trialId, reportKinds, includePreScreeningAndScreenFailed }),
  });

// This poll is also the tab-liveness heartbeat the backend's periodic sweep watches for (a
// silence of 15 minutes is treated as an abandoned tab and frees the slot) -- refetchIntervalInBackground
// keeps it firing while the tab is merely backgrounded, not closed, so it doesn't get reaped for that.
export const useStudyExportStatusQuery = (exportRequestId, { enabled = true } = {}) =>
  useQuery({
    queryKey: ["study-export-status", exportRequestId],
    enabled: enabled && Boolean(exportRequestId),

    queryFn: ({ signal }) => getStudyExportStatus({ exportRequestId, signal }),

    refetchInterval: (q) => {
      const s = q?.state?.data?.status;
      if (s === "SUCCESS" || s === "FAILED") return false;
      return 10000;
    },
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

export const useReportEcrfFilledPdfOutcomeMutation = () =>
  useMutation({
    mutationFn: ({ exportRequestId, reason, error }) =>
      reportEcrfFilledPdfOutcome({ exportRequestId, reason, error }),
  });

export const useReportPoemFilledPdfOutcomeMutation = () =>
  useMutation({
    mutationFn: ({ exportRequestId, reportKind, reason, error }) =>
      reportPoemFilledPdfOutcome({ exportRequestId, reportKind, reason, error }),
  });

export const useCancelStudyExportMutation = () =>
  useMutation({
    mutationFn: ({ exportRequestId }) => cancelStudyExport({ exportRequestId }),
  });
